// TelcoTrack — seskupení skladů: neosobní / osobní
//
// Osobní = linkedUserId (users.warehouse_id) nebo heuristika jména osoby.
// Neosobní = vše ostatní (bez kategorií).

export type WarehouseUiGroup = 'branches' | 'workers';

/** UI popisky skupin. */
export const WAREHOUSE_UI_GROUP_LABELS: Record<WarehouseUiGroup, string> = {
  branches: 'Neosobní',
  workers: 'Osobní',
};

const COMPANY_OR_PLACE_HINT =
  /protelco|fiber|s\.?\s*r\.?\s*o\.?|a\.?\s*s\.?|sklad|centrála|centrala|pobočka|pobocka|\bvan\b|firma|warehouse/i;
const PERSON_NAME_TOKEN = /^[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ][a-záčďéěíňóřšťúůýž'-]+$/u;

export interface WarehouseUiGroupInput {
  name?: string | null;
  type?: string | null;
  /** TelcoTrack user linked via `users.warehouse_id` → osobní sklad. */
  linkedUserId?: string | null;
}

/** true = osobní sklad (auto-provision / users.warehouse_id). */
export function isPersonalWarehouse(wh: WarehouseUiGroupInput): boolean {
  return resolveWarehouseUiGroup(wh) === 'workers';
}

/** DELETE skladu — osobní sklady (linkedUserId / users.warehouse_id) nelze smazat. */
export const WAREHOUSE_DELETE_DENIED_PERSONAL_CS =
  'Tento sklad nelze smazat (osobní sklad)';

export function resolveWarehouseUiGroup(wh: WarehouseUiGroupInput): WarehouseUiGroup {
  const linked = (wh.linkedUserId ?? '').trim();
  if (linked) return 'workers';

  const type = String(wh.type ?? '').trim().toLowerCase();
  if (type === 'positional') return 'branches';

  const name = (wh.name ?? '').trim();
  if (looksLikeWorkerWarehouseName(name)) return 'workers';
  return 'branches';
}

/** Neosobní před osobními; uvnitř skupiny stabilně podle `name` (cs). */
export function compareWarehousesByUiGroup(
  a: WarehouseUiGroupInput,
  b: WarehouseUiGroupInput,
): number {
  const ga = resolveWarehouseUiGroup(a);
  const gb = resolveWarehouseUiGroup(b);
  if (ga !== gb) return ga === 'branches' ? -1 : 1;
  return (a.name || '').localeCompare(b.name || '', 'cs');
}

export function sortWarehousesByUiGroup<T extends WarehouseUiGroupInput>(
  list: T[] | null | undefined,
): T[] {
  return [...(list ?? [])].sort(compareWarehousesByUiGroup);
}

/** Heuristika při chybějícím linkedUserId (např. před enrich). */
export function looksLikeWorkerWarehouseName(name: string | null | undefined): boolean {
  const n = (name ?? '').trim();
  if (!n || COMPANY_OR_PLACE_HINT.test(n)) return false;
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length < 2 || parts.length > 3) return false;
  return parts.every((p) => PERSON_NAME_TOKEN.test(p));
}
